import { isLogMealById } from '@cf-shared/data-model/food';
import { Ingredient } from '@cf-shared/data-model/ingredient';
import { EditIngredientMode, EditIngredientPageParams } from './edit-ingredient-interface';

/**
 * What a caller has to say to open the portion screen in Log mode. Everything else is
 * constant and is filled in by `buildLogPortionScreenParams`.
 */
export interface LogPortionScreenRequest {
  /** The day the food is logged to, as an ISO date (`2026-09-08`). */
  dayDate: string;
  /**
   * What the user picked. This is a food id most of the time, but the food list also offers
   * RECENT MEALS, whose ids read `log/meal/...`, and the portion screen takes those under a
   * different parameter. Which one it is decided here, so no caller has to remember.
   *
   * Absent only when there is nothing to open on — the Log Macros Manually route, which
   * starts from `pendingIngredient` and an empty form.
   */
  pickedId?: string;
  /**
   * Put the food in THIS meal rather than the day's default one. Used by the routes that
   * start inside one particular meal.
   */
  selectedMealId?: string;
  /** An ingredient the user has not filled in yet — the Log Macros Manually route. */
  pendingIngredient?: Ingredient;
}

/**
 * Build the parameters for opening the portion screen (`IEditIngredientPage`) in Log mode.
 *
 * Log mode means the screen writes the day itself: it saves the meal, announces the change and
 * offers an Undo, so the caller has nothing to persist and no answer to wait for.
 *
 * This exists because three of its parameters never vary and getting one wrong fails SILENTLY
 * rather than at compile time — `EditIngredientPageParams` has them optional, so an object
 * missing `ingredientIndex: -1` still type-checks and then edits ingredient number 0 instead of
 * adding a new one. There were 4 hand-written copies of this object before it (deferred finding
 * df-e1e2706df0aa), each of which had to remember all three, plus the recent-meal branch below,
 * which had already been got wrong once on a route that shipped without it.
 */
export function buildLogPortionScreenParams(request: LogPortionScreenRequest): EditIngredientPageParams {
  return {
    mode: EditIngredientMode.Log,
    // -1 means "this is a new ingredient", as opposed to an index into an existing list
    ingredientIndex: -1,
    dayDate: request.dayDate,
    ...(request.selectedMealId ? { selectedMealId: request.selectedMealId } : {}),
    // a recent meal is copied in through logMealId; a food is read through foodId. Passing a
    // meal id as a food id makes the screen's food read fail and pop itself again, so the user
    // taps a recent meal, sees a flash, and gets nothing added.
    ...(request.pickedId === undefined
      ? {}
      : isLogMealById(request.pickedId)
        ? { logMealId: request.pickedId }
        : { foodId: request.pickedId }),
    local: {
      // Log mode starts from an empty list: the screen is adding to the day, not editing a
      // list the caller is holding.
      ingredients: [],
      ...(request.pendingIngredient ? { pendingIngredient: request.pendingIngredient } : {}),
    },
  };
}
